/**
 * ClockTool.jsx — Cycle 55 · Teach & Explain
 * ──────────────────────────────────────────────
 * Teacher-facing analogue clock manipulative. Draggable minute hand
 * drives the hour hand proportionally (0.5° per minute). Digital
 * toggle underneath. No score, no timer — pure demo.
 *
 * State: ?h=3&m=45&digital=1
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ManipulativeFrame from "@/components/tools/ManipulativeFrame";

const clampInt = (v, lo, hi, def) => {
  const n = parseInt(v ?? "", 10);
  if (isNaN(n)) return def;
  return Math.max(lo, Math.min(hi, n));
};

const PAD = (n) => String(n).padStart(2, "0");

function ClockFace({ h, m, size = 300, onChange, readOnly = false }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 6;
  const minAngle = m * 6;
  const hourAngle = ((h % 12) * 30) + (m * 0.5);
  const svgRef = useRef(null);
  const draggingRef = useRef(null); // "minute" | "hour" | null

  const angleFromEvent = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    const cxPx = rect.left + rect.width / 2;
    const cyPx = rect.top + rect.height / 2;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - cxPx;
    const dy = clientY - cyPx;
    // Angle from 12 o'clock, clockwise, in degrees.
    const rad = Math.atan2(dx, -dy);
    let deg = rad * (180 / Math.PI);
    if (deg < 0) deg += 360;
    return deg;
  }, []);

  const startDrag = (which) => (e) => {
    if (readOnly) return;
    e.preventDefault();
    draggingRef.current = which;
  };

  useEffect(() => {
    if (readOnly) return;
    const move = (e) => {
      if (!draggingRef.current) return;
      const deg = angleFromEvent(e);
      if (draggingRef.current === "minute") {
        // Snap to nearest minute.
        const mm = Math.round(deg / 6) % 60;
        // If the minute crosses over 0 upward, hour advances; downward, decreases.
        const prev = m;
        let newH = h;
        if (prev >= 45 && mm <= 15) newH = (h + 1) % 24;
        else if (prev <= 15 && mm >= 45) newH = (h + 23) % 24;
        onChange({ h: newH, m: mm });
      } else if (draggingRef.current === "hour") {
        // Hour hand: 30° per hour. Snap to nearest hour so it feels tight.
        const hh = Math.round(deg / 30) % 12;
        onChange({ h: hh === 0 ? 12 : hh, m });
      }
    };
    const end = () => { draggingRef.current = null; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
  }, [h, m, angleFromEvent, onChange, readOnly]);

  const minEnd = {
    x: cx + (r - 24) * Math.sin(minAngle * Math.PI / 180),
    y: cy - (r - 24) * Math.cos(minAngle * Math.PI / 180),
  };
  const hourEnd = {
    x: cx + (r - 60) * Math.sin(hourAngle * Math.PI / 180),
    y: cy - (r - 60) * Math.cos(hourAngle * Math.PI / 180),
  };

  return (
    <svg ref={svgRef} width={size} height={size} viewBox={`0 0 ${size} ${size}`}
         data-testid="clocktool-face" className="touch-none select-none">
      <circle cx={cx} cy={cy} r={r} fill="#fef9c3" stroke="#0f172a" strokeWidth="6" />
      {/* Minute marks + numerals */}
      {Array.from({ length: 60 }).map((_, i) => {
        const a = (i * 6) * (Math.PI / 180);
        const x1 = cx + (r - 6) * Math.sin(a);
        const y1 = cy - (r - 6) * Math.cos(a);
        const x2 = cx + r * Math.sin(a);
        const y2 = cy - r * Math.cos(a);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0f172a" strokeWidth={i % 5 === 0 ? 4 : 1.5} strokeLinecap="round" />;
      })}
      {Array.from({ length: 12 }).map((_, i) => {
        const a = ((i + 1) * 30) * (Math.PI / 180);
        return (
          <text key={i}
                x={cx + (r - 26) * Math.sin(a)}
                y={cy - (r - 26) * Math.cos(a) + 6}
                textAnchor="middle"
                fontFamily="ui-sans-serif, system-ui" fontWeight="900" fontSize="18" fill="#0f172a">
            {i + 1}
          </text>
        );
      })}

      {/* Hour hand — bigger tap target invisible line + visible thick line */}
      <line
        x1={cx} y1={cy} x2={hourEnd.x} y2={hourEnd.y}
        stroke="transparent" strokeWidth="26" strokeLinecap="round"
        onMouseDown={startDrag("hour")} onTouchStart={startDrag("hour")}
        style={{ cursor: readOnly ? "default" : "grab" }}
        data-testid="clocktool-hour-hitbox"
      />
      <line
        x1={cx} y1={cy} x2={hourEnd.x} y2={hourEnd.y}
        stroke="#e11d48" strokeWidth="8" strokeLinecap="round"
        pointerEvents="none"
      />

      {/* Minute hand */}
      <line
        x1={cx} y1={cy} x2={minEnd.x} y2={minEnd.y}
        stroke="transparent" strokeWidth="26" strokeLinecap="round"
        onMouseDown={startDrag("minute")} onTouchStart={startDrag("minute")}
        style={{ cursor: readOnly ? "default" : "grab" }}
        data-testid="clocktool-minute-hitbox"
      />
      <line
        x1={cx} y1={cy} x2={minEnd.x} y2={minEnd.y}
        stroke="#0f172a" strokeWidth="5" strokeLinecap="round"
        pointerEvents="none"
      />
      <circle cx={cx} cy={cy} r={9} fill="#0f172a" pointerEvents="none" />
    </svg>
  );
}

// Prop-driven display (also used by ManipulativeEmbed).
export function ClockDisplay({ h, m, digital, setTime, readOnly = false }) {
  const h12 = ((h + 11) % 12) + 1;
  const wordTime = (() => {
    if (m === 0) return `${h12} o'clock`;
    if (m === 15) return `Quarter past ${h12}`;
    if (m === 30) return `Half past ${h12}`;
    if (m === 45) return `Quarter to ${(h12 % 12) + 1}`;
    if (m < 30) return `${m} past ${h12}`;
    return `${60 - m} to ${(h12 % 12) + 1}`;
  })();
  return (
    <div className="rounded-3xl border-[3px] border-slate-900 bg-white p-4 md:p-6 shadow-[4px_4px_0_0_rgba(15,23,42,1)]" data-testid="clocktool-canvas">
      <div className="flex justify-center">
        <ClockFace h={h} m={m} onChange={setTime} readOnly={readOnly} />
      </div>
      {digital && (
        <div className="mt-4 text-center">
          <p className="font-heading text-4xl md:text-5xl font-black tabular-nums text-slate-900" data-testid="clocktool-digital">
            {PAD(h12)}:{PAD(m)}
          </p>
          <p className="mt-1 text-sm font-bold text-slate-600" data-testid="clocktool-word">{wordTime}</p>
        </div>
      )}
    </div>
  );
}

export default function ClockTool() {
  const [params, setParams] = useSearchParams();
  const h = clampInt(params.get("h"), 1, 12, 3);
  const m = clampInt(params.get("m"), 0, 59, 0);
  const digital = params.get("digital") !== "0";

  const update = (patch) => {
    const q = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) q.delete(k); else q.set(k, String(v));
    }
    setParams(q, { replace: true });
  };
  const setTime = ({ h: nh, m: nm }) => update({ h: nh, m: nm });

  const settingsPanel = (
    <div className="flex flex-wrap items-center gap-2">
      <label className="text-xs font-bold text-slate-700">Hour
        <input type="number" min="1" max="12" value={h} data-testid="clocktool-h"
               onChange={(e) => update({ h: clampInt(e.target.value, 1, 12, 3) })}
               className="ml-2 h-9 w-20 px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
      </label>
      <label className="text-xs font-bold text-slate-700">Minute
        <input type="number" min="0" max="59" value={m} data-testid="clocktool-m"
               onChange={(e) => update({ m: clampInt(e.target.value, 0, 59, 0) })}
               className="ml-2 h-9 w-20 px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
      </label>
      <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
        <input type="checkbox" checked={digital} data-testid="clocktool-digital-toggle"
               onChange={(e) => update({ digital: e.target.checked ? null : "0" })}
               className="h-4 w-4" />
        Show digital
      </label>
    </div>
  );

  return (
    <ManipulativeFrame
      slug="clock"
      title="Clock"
      description="Drag the minute or hour hand. The hour hand moves proportionally between numbers. Toggle the digital readout below."
      onReset={() => update({ h: null, m: null, digital: null })}
      settingsPanel={settingsPanel}
      shareUrl={window.location.href}
    >
      <ClockDisplay h={h} m={m} digital={digital} setTime={setTime} />
    </ManipulativeFrame>
  );
}
